import { tokensToCss } from "@verb/theme";

/**
 * Widget styles, injected as a constructed stylesheet into the shadow root.
 *
 * Nothing here can reach the customer's page and nothing of theirs can reach us
 *, that is the whole reason for Shadow DOM over a plain div. It is NOT an
 * iframe because an iframe would kill same-origin execution, which is the
 * security story.
 *
 * Tokens come from @verb/theme so the widget and the dashboard cannot drift.
 * A customer can override any of them on the host element to match their brand;
 * the focus ring deliberately is not weakened by that.
 */
export const styles = `
${tokensToCss(":host")}

:host {
  all: initial;
  font-family: var(--verb-font);
  color: var(--verb-text);
  --verb-panel-w: 400px;
}

/* 🔴 scrollbar-color: auto IS HERE BECAUSE A CUSTOMER'S PAGE MADE EVERY
   SCROLLBAR IN THE PANEL INVISIBLE, 2026-09-24. Resuelve's stylesheet has
   *{scrollbar-color:var(--border) transparent}, and on their dark theme
   --border is #ffffff1a, white at 10%. Their * matches our HOST element, and
   scrollbar-color is inherited, so it flowed straight through the shadow
   boundary: the table's scrollbar and the transcript's were both painted 10%
   white on a light panel. It also switches off the ::-webkit-scrollbar rules
   below, since Chrome ignores those once the standard property is set.
   :host { all: initial } does not stop it, because the page's own rules on
   the host outrank :host. Only elements INSIDE the shadow root are out of the
   page's reach, which is why the reset sits on this selector. */
*, *::before, *::after { box-sizing: border-box; scrollbar-color: auto; }

button { font: inherit; color: inherit; cursor: pointer; }

/* 🔴 THE Z-INDEX LIVES HERE, ON THE FIXED ELEMENTS, NOT ON THE HOST.
 *
 *    The host carried position:static with z-index 2147483000, and z-index has
 *    NO EFFECT on a statically positioned element. It was inert. Both of these
 *    rules are position:fixed with no z-index of their own, so they sat at auto,
 *    and any positioned element on the customer page with a z-index above 0
 *    covered them.
 *
 *    Found on a real admin panel whose sticky header drew straight over the top
 *    of the panel. It looked like the customer CSS misbehaving and it was ours:
 *    we believed we had set the highest z-index on the page and had set none.
 *
 *    Declared inside the shadow root, where their stylesheet cannot reach it,
 *    and just below the 32-bit maximum so they can still put something above us
 *    deliberately. */
.launcher {
  /* --verb-offset-x / -y come from data-verb-offset-x / -y on the script tag,
     for a corner shared with a cookie banner or another chat bubble. */
  position: fixed; right: var(--verb-offset-x, 20px); bottom: var(--verb-offset-y, 20px);
  z-index: 2147483000;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 12px 18px; border: none;
  border-radius: 999px;
  /* 🔴 THE BUTTON PAINTS ITS OWN FILL AND ALWAYS HAS TO. Two versions in a
     row moved it somewhere else so the glow could sit underneath, and the
     second one shipped a launcher with NO COLOUR AT ALL on Vishnus screen.

     The mechanism is worth writing down because it is invisible in review. A
     background whose value contains a var() that does not resolve is INVALID
     AT COMPUTED-VALUE TIME, and that does not fall back to the earlier
     declaration in the cascade, it resolves to unset. Unset for background is
     transparent. So one unresolvable custom property anywhere in that chain,
     on any engine, at any point in the load, and the fill is simply gone: a
     colourless pill with dark text and a shadow, which is what was
     photographed.

     The glow is layers ON TOP of a button that is already correct. If every
     one of them fails to render, the worst case is a plain gold button. */
  background: var(--verb-gold);
  /* Computed per brand colour by deriveBrandTokens, not assumed: the default
     near-black on gold is ~8.5:1, but white on gold would be ~2:1 and fail
     AA, the single most common way a yellow palette ships inaccessible. A
     site that picks its own colour gets whichever of near-black or
     near-white actually clears contrast against it, not this fallback. */
  color: var(--verb-gold-fg, var(--verb-text));
  font-weight: 600; font-size: 14px;
  box-shadow: var(--verb-shadow);
  /* 🔴 IT DOES NOT PAINT UNTIL WE KNOW WHAT COLOUR IT IS. The brand colour,
     the background and the glow are all set in the dashboard and arrive from
     /v1/config, which is a network round trip AFTER the script has run. The
     launcher used to render immediately in VERB'S OWN GOLD and then repaint in
     the customer's colour a beat later, so every visitor to a site with a
     custom colour watched a yellow button turn into a different button.

     Painting a stranger's brand colour on somebody else's page, even for
     200ms, is the wrong default: gold is our colour, not theirs. Waiting
     costs about the length of one request and is invisible; repainting is
     not. See markReady, which reveals it as soon as the config settles either
     way, with a timer behind it so a request that never returns cannot leave
     the page with no button at all. */
  opacity: 0;
  visibility: hidden;
  transition: opacity 160ms ease, background 120ms ease, transform 120ms ease,
    box-shadow 120ms ease;
}
.launcher[data-ready="true"] { opacity: 1; visibility: visible; }

/* The line that leans out of the button before anyone has opened it.
   🔴 IT MIRRORS THE LAUNCHER'S CORNER AND MUST KEEP MIRRORING IT. A bubble
      pinned bottom-right beside a button somebody moved to the top-left is
      not a teaser, it is a second widget. Panel#setPosition writes the same
      data-position onto both.
   🔴 THE OFFSET IS THE BUTTON'S OWN HEIGHT PLUS A GAP, and it is written
      against the same --verb-offset-* variables the launcher uses, so a site
      that moved the button out of the way of its cookie banner moves this
      with it rather than leaving it floating over the old spot. */
.teaser {
  position: fixed; z-index: 2147483000;
  right: var(--verb-offset-x, 20px);
  bottom: calc(var(--verb-offset-y, 20px) + var(--verb-face-lift, 62px));
  max-width: min(260px, calc(100vw - 40px));
  display: flex; align-items: flex-start; gap: 4px;
  padding: 9px 9px 9px 12px;
  background: var(--verb-bg); color: var(--verb-text);
  border: 1px solid var(--verb-border);
  border-radius: var(--verb-radius);
  box-shadow: var(--verb-shadow);
  font-size: 13px; line-height: 1.45;
  opacity: 0; transform: translateY(4px);
  transition: opacity .18s ease, transform .18s ease;
}
/* display:flex above beats the hidden attribute outright, so hidden would
   draw the bubble anyway. hiddenAttribute.test.ts exists for exactly this. */
.teaser[hidden] { display: none; }
.teaser[data-show="true"] { opacity: 1; transform: none; }
.teaser[data-position="bottom-left"] { right: auto; left: var(--verb-offset-x, 20px); }
.teaser[data-position="top-right"] { bottom: auto; top: calc(var(--verb-offset-y, 20px) + var(--verb-face-lift, 62px)); }
.teaser[data-position="top-left"] {
  right: auto; left: var(--verb-offset-x, 20px);
  bottom: auto; top: calc(var(--verb-offset-y, 20px) + var(--verb-face-lift, 62px));
}
/* The sentence is the button: the whole point is that it is easier to open
   than the button it is pointing at. */
.teaser-open {
  flex: 1 1 auto; min-width: 0;
  background: none; border: none; padding: 0; margin: 0;
  font: inherit; color: inherit; text-align: left; cursor: pointer;
}
.teaser-close {
  flex: none; width: 18px; height: 18px; padding: 0;
  display: grid; place-items: center;
  background: none; border: none; border-radius: 999px;
  color: var(--verb-text-muted); font-size: 15px; line-height: 1; cursor: pointer;
}
.teaser-close:hover { background: var(--verb-surface-alt); color: var(--verb-text); }
.teaser-open:focus-visible, .teaser-close:focus-visible {
  outline: 2px solid var(--verb-gold-ring); outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) { .teaser { transition: none; } }

/* Launcher corners. Bottom-right is the CSS above and needs no override; the
   other three replace right/bottom with the opposite edges. Purely the
   button's own placement: which edge the drawer docks to follows the LEFT/
   RIGHT half below, independent of top/bottom.

   🔴 THESE WERE DELETED BY ACCIDENT ON 2026-09-07 AND SHIPPED. The glow
   rewrite replaced everything between the .launcher rule and .panel, and this
   block was inside that range. Nothing failed: the panel keeps its own corner
   rules, so a site set to bottom-left got a drawer docked correctly on the
   left and a button still floating bottom-right, which looks like a
   positioning preference rather than a bug. No test covered it because the
   position is set as a data attribute and asserted there, which stayed true
   the whole time. */
.launcher[data-position="bottom-left"]:not([data-inline="true"]) {
  right: auto; left: var(--verb-offset-x, 20px);
}
.launcher[data-position="top-right"]:not([data-inline="true"]) {
  bottom: auto; top: var(--verb-offset-y, 20px);
}
.launcher[data-position="top-left"]:not([data-inline="true"]) {
  right: auto; left: var(--verb-offset-x, 20px); bottom: auto; top: var(--verb-offset-y, 20px);
}

/**
 * 🔴 THE BUTTON DOES NOT HAVE TO FLOAT IN A CORNER. Four corners is the right
 *    default and the wrong ceiling: plenty of products already have a place
 *    this belongs, in a nav bar, beside a page title, in a toolbar, and
 *    pinning it to a corner instead puts it somewhere their design did not ask
 *    for, on top of whatever was already there.
 *
 *    data-verb-launcher takes a CSS selector and the button is rendered INTO
 *    that element, laid out by the customer's own stylesheet like any other
 *    child. Everything else about it is unchanged, the glow included.
 *
 * 🔴 IT WAS ALREADY POSSIBLE AND NOBODY COULD HAVE GUESSED HOW. Verb.open()
 *    and data-verb-hide-launcher have both existed for months, so a customer
 *    could hide ours and wire their own button to it. That is a fine escape
 *    hatch and a bad answer to "can I put it in my header", because it asks
 *    somebody to write JavaScript in order to move a button.
 */
.launcher[data-inline="true"] {
  position: static;
  right: auto; left: auto; top: auto; bottom: auto;
  /* The lift was for a button hovering over a page. Once it is IN the page,
     among the customer's own controls, a drop shadow is just wrong. */
  box-shadow: none;
}
.launcher[data-inline="true"]:hover { transform: none; box-shadow: none; }
.launcher:hover {
  background: var(--verb-gold-hover);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgb(28 25 23 / 8%), 0 20px 48px -12px rgb(28 25 23 / 30%);
}
.launcher:active { transform: translateY(0); }
.launcher:focus-visible { outline: 3px solid var(--verb-gold-ring); outline-offset: 2px; }

/* ── The glow ─────────────────────────────────────────────────
 *
 * 🔴 THE RAINBOW WAS ASKED FOR TWICE AND REFUSED ONCE. The first time it was
 *    shipped as a gold sheen instead, on the argument that a cycling spectrum
 *    renders next to a customer's own buttons and clashes with every brand it
 *    lands on. That argument still holds, which is why this is OFF by default
 *    and why "brand" exists beside it. It is not a reason to keep overruling
 *    somebody about their own product, so "rainbow" is here and it is real.
 *
 * 🔴 NO REGISTERED CUSTOM PROPERTY, AND THAT IS THE WHOLE POINT OF THIS
 *    VERSION. The first two animated an @property angle inside a
 *    conic-gradient. It is the tidy way to write it and it has two failure
 *    modes that both showed up: the gradient repaints on the main thread every
 *    frame, which strobes under a blur, and if the registration is not in
 *    effect the var() is unresolvable and takes the entire background
 *    declaration down with it. See the note on .launcher above for why that
 *    ends in an invisible button.
 *
 *    A square bigger than the pill, spun with the rotate property and
 *    clipped by an overflow:hidden parent, produces the same moving ring. It
 *    is a compositor animation, so it cannot strobe, and the gradient itself
 *    is static, so there is no var() in it that can fail. Nothing here is
 *    load-bearing for the button being visible.
 *
 * 🔴 AND IT STOPS UNDER prefers-reduced-motion. A continuous loop is a
 *    vestibular trigger and this one is pure decoration, so the animation goes
 *    away and the colour stays.
 */
@keyframes verb-glow-spin { to { rotate: 360deg; } }

/* Both layers exist for every site and are invisible until asked for, so
   turning the glow on is an opacity change on something already in the tree
   rather than a repaint of the button. That is what stops it appearing with a
   visible jump when /v1/config resolves after first paint. */
.launcher-halo, .launcher-ring {
  position: absolute;
  border-radius: 999px;
  overflow: hidden;
  opacity: 0;
  transition: opacity 420ms ease;
  pointer-events: none;
}
.launcher-halo { inset: -7px; z-index: 0; filter: blur(10px); }
.launcher-ring { inset: 0; z-index: 1; }

/* The spinning square. Wider than the pill's own diagonal at every angle, so
   the clip never exposes a corner. */
.launcher-halo > i, .launcher-ring > i {
  position: absolute; top: 50%; left: 50%;
  width: 280%; aspect-ratio: 1;
  translate: -50% -50%;
  background: conic-gradient(
    var(--verb-gold-soft), var(--verb-gold-ring), var(--verb-gold),
    var(--verb-gold-hover), var(--verb-gold), var(--verb-gold-ring),
    var(--verb-gold-soft));
  animation: verb-glow-spin 4.5s linear infinite;
}
.launcher[data-glow="rainbow"] > .launcher-halo > i,
.launcher[data-glow="rainbow"] > .launcher-ring > i {
  background: conic-gradient(
    #ff4d4d, #ff9f1c, #ffe45c, #4ade80, #22d3ee, #6366f1, #d946ef, #ff4d4d);
}

/* The face, over the ring, leaving it showing as a 2px band. Its own solid
   colour rather than the button's, so the ring is clipped by geometry rather
   than by anything that can fail to compute. */
.launcher-face {
  position: absolute; inset: 2px;
  border-radius: 999px;
  background: var(--verb-gold);
  z-index: 2;
  opacity: 0;
  transition: opacity 420ms ease;
  pointer-events: none;
}
.launcher:hover > .launcher-face { background: var(--verb-gold-hover); }

/* The attribute is only ever present for "brand" and "rainbow"; off removes
   it, so every rule here can match on the bare attribute. */
.launcher[data-glow] > .launcher-halo { opacity: .85; }
.launcher[data-glow] > .launcher-ring,
.launcher[data-glow] > .launcher-face { opacity: 1; }

/* Above all three, which are positioned and would otherwise paint over the
   icon and the words. */
/* 🔴 A FIXED BOX, NEVER A PERCENTAGE. height:100% inside a button with no
   height of its own computes to auto, the image collapses, and the launcher
   becomes an invisible pill: present, clickable, and impossible to see. That
   shipped to Vishnu's screen on 2026-10-06 and read as "the widget is gone".
   The picture gets a square the size of the pill's natural height instead. */
/* 🔴 WITH A PICTURE ON IT, THE BUTTON IS THE PICTURE AND NOTHING ELSE.
   Our gold fill stayed underneath and showed as a yellow rim around the
   image, because two 52px circles never antialias to the same pixels and
   any transparent edge in the upload let the fill straight through (Vishnu,
   2026-10-06, on the Cal demo: "I can see some yellow, like it is hiding
   behind the uploaded image"). Transparent here rather than a colour, plus
   the shadow and the gold face layer off: the site chose this image, so the
   button shows it and adds nothing of ours to it.
   The hover fill has to go too, or the yellow comes back under the cursor. */
/* --verb-face-size: the site's size for the face, 52px to 104px (migration 0068). */
.launcher.has-img{padding:0;width:var(--verb-face-size,52px);height:var(--verb-face-size,52px);justify-content:center}
/* 🔴 OUR FILL GOES ONLY ONCE THE PICTURE IS ACTUALLY THERE. img-ready is
   added on the image's load event, never with has-img, because a button that
   is transparent while a slow avatar downloads is an INVISIBLE BUTTON, which
   is the exact regression boot.test.ts guards against and which shipped once
   already. Gold until the picture lands, the picture alone afterwards. */
.launcher.img-ready{background:transparent;box-shadow:none}
.launcher.img-ready:hover{background:transparent;box-shadow:none}
.launcher.img-ready>.launcher-face{display:none}
.launcher.has-img .launcher-label{padding:0;gap:0;width:100%;height:100%}
/* 🔴 THE WORDS WERE A BARE TEXT NODE, AND NO SELECTOR REACHES ONE. This rule
   used to target a child SPAN of the label, matching nothing, because "Ask AI" sat
   loose inside the label rather than in an element. The sparkle hid, the words
   did not, and they held their place inside the 52px square and shoved the
   picture out of it, half off the screen (cal.askverb.com, 2026-10-06). The
   words are wrapped in .launcher-text now so there is something to select.
   🔴 HIDDEN FROM EYES, NOT FROM SCREEN READERS. The button carries no
   aria-label, so these words ARE its accessible name; display:none here would
   leave an unnamed button. Same declarations as .sr-only below. */
.launcher.has-img .launcher-label>svg:not(.blob){display:none}
.launcher.has-img .launcher-text{
  position:absolute; width:1px; height:1px; padding:0; margin:-1px;
  overflow:hidden; clip:rect(0 0 0 0); white-space:nowrap; border:0;
}
.launcher-img{width:100%;height:100%;border-radius:999px;object-fit:cover;display:block}
/* A blob (blob.ts, migration 0067) is the body alone, no plate, so nothing clips it round and its
   shadow follows its own outline: a pale blob on a white page still needs an edge. */
.launcher.img-ready>.launcher-label>svg.blob{border-radius:0;overflow:visible;filter:drop-shadow(0 3px 5px rgba(0,0,0,.22))}
.launcher-label {
  position: relative; z-index: 3;
  display: inline-flex; align-items: center; gap: 8px;
}

@media (prefers-reduced-motion: reduce) {
  .launcher-halo, .launcher-ring, .launcher-face { transition: none; }
  .launcher-halo > i, .launcher-ring > i { animation: none; }
}

.panel {
  position: fixed; top: 0; right: 0; bottom: 0;
  z-index: 2147483000;
  width: var(--verb-panel-w); max-width: 100vw;
  display: flex; flex-direction: column;
  background: var(--verb-bg);
  border-left: 1px solid var(--verb-border);
  box-shadow: var(--verb-shadow);
  /* Overlay-by-default is handled in JS now (Panel#markPageState pushes
     <body> aside); this transform only ever animates the DRAWER's own
     entrance, never the page underneath it. */
  transform: translateX(100%);
  transition: transform 180ms cubic-bezier(.32,.72,0,1);
  /* 🔴 AN ANSWER NOBODY CAN COPY IS HALF AN ANSWER, and whether it can be
     copied is not entirely ours to decide. user-select does not inherit, but
     its initial auto value RESOLVES AGAINST THE PARENT'S USED VALUE, and our host
     element lives in the customer's page, so an app that sets user-select:none
     on a wrapper above us turns every reply into dead text inside our own
     shadow root. Plenty of app shells do, to stop drags selecting their
     chrome. Declared outright here so the answer is always selectable whatever
     the page around it says. The -webkit- pair is not legacy padding: iOS
     Safari still needs it. */
  -webkit-user-select: text;
  user-select: text;
}
.panel[data-open="true"] { transform: translateX(0); }

/* A drawer only meaningfully docks to a SIDE, not a corner: it is always
   full height. A left-corner launcher pulls it to the left edge instead. */
.panel[data-position="bottom-left"], .panel[data-position="top-left"] {
  right: auto; left: 0;
  border-left: none;
  border-right: 1px solid var(--verb-border);
  transform: translateX(-100%);
}
.panel[data-position="bottom-left"][data-open="true"],
.panel[data-position="top-left"][data-open="true"] {
  transform: translateX(0);
}

/**
 * 🔴 A REAL SECOND LAYOUT, NOT A TRANSFORM VARIANT OF THE DRAWER.
 *
 *    Centred, fixed size, rounded on all four corners, the shape a Cmd+K
 *    palette or Kapa's own widget uses: right for a pure answer-and-close
 *    tool, wrong for an assistant that acts on data you want to keep looking
 *    at (see the note in Panel#setDisplayMode). Opacity and scale, not
 *    translateX: nothing here is docked to an edge to slide in from.
 */
/**
 * 🔴 IT IS A MODAL NOW, WITH A BACKDROP, AND THAT IS THE PART THAT WAS MISSING
 *    RATHER THAN THE SIZE. A box floating over a page with nothing behind it
 *    reads as unfinished no matter how big it is: nothing tells the eye the
 *    rest of the page is on hold, so it looks like a widget that failed to
 *    dock. Every modal a visitor has ever used dims what is behind it, and
 *    that dimming is what makes a centred box legible as a centred box.
 *
 * 🔴 CENTRED AGAIN, REVERSING THE ANCHORING OF 2026-09-07. That change argued
 *    a popup should grow out of the button that opened it, which is true of a
 *    small menu and false of something this size: at 760px wide there is no
 *    corner it can grow from without hanging off two edges of a laptop screen.
 *    With a scrim the relationship to the launcher stops mattering, because
 *    the backdrop already says where attention goes.
 *
 * Opacity and scale, never translateX: nothing here is docked to an edge to
 * slide in from, and the four launcher corners do not apply to it at all.
 */
.scrim {
  position: fixed; inset: 0;
  z-index: 2147482999; /* one under the panel, so it never covers it */
  background: rgb(28 25 23 / 55%);
  opacity: 0;
  pointer-events: none;
  transition: opacity 180ms ease;
}
.scrim[data-open="true"] { opacity: 1; pointer-events: auto; }

/* 🔴 ONLY IN DIALOG MODE, NEVER BEHIND THE DRAWER. The drawer pushes the
   page aside rather than covering it, precisely so somebody can keep looking
   at the thing they are asking about. Dimming it would take that back. The
   state is set in JS; this is the floor under it. */
.panel[data-display-mode="panel"] ~ .scrim { display: none; }

.panel[data-display-mode="dialog"] {
  top: 50%; left: 50%; right: auto; bottom: auto;
  /* Room for a confirmation card and a real answer side by side with the
     question that caused them. The drawer is 400px; a modal that asked for
     the whole screen and then gave less width than that would be absurd. */
  width: min(760px, calc(100vw - 40px));
  height: min(640px, calc(100vh - 80px));
  max-width: 100vw;
  /* The shorthand, not per-side properties: a left-docked position variant
     may have set border-left to none, and the shorthand here overrides all
     four sides at once regardless of which one a position rule touched. */
  border: 1px solid var(--verb-border);
  border-radius: 14px;
  box-shadow: 0 4px 12px rgb(28 25 23 / 10%), 0 32px 80px -20px rgb(28 25 23 / 45%);
  transform: translate(-50%, -50%) scale(0.98);
  opacity: 0;
  pointer-events: none;
  transition: transform 180ms cubic-bezier(.32,.72,0,1), opacity 150ms ease;
}

/* Last, so it beats every launcher-corner rule above at equal specificity. */
.panel[data-display-mode="dialog"][data-open="true"] {
  transform: translate(-50%, -50%) scale(1);
  opacity: 1;
  pointer-events: auto;
}

@media (max-width: 640px) {
  /* A 400px drawer, or a fixed-size dialog, is unusable on a phone: both
     modes go full-screen the same way. */
  /* 🔴 MEASURED AGAINST THE VISUAL VIEWPORT, so an open keyboard shrinks the
     sheet instead of pushing its composer underneath the keys. The variables
     are set by Panel#trackViewport while the panel is open and removed when it
     closes; without them (no visualViewport, or closed) this falls back to the
     edge-pinned behaviour it always had. */
  .panel {
    width: 100vw; border-left: none; border-right: none;
    top: var(--verb-vv-top, 0);
    /* bottom:auto, or a sheet pinned top AND bottom ignores the height set
       here and goes back to spanning the layout viewport. 100dvh is the
       fallback where visualViewport is missing: it already follows the
       keyboard on current browsers, just less precisely. */
    bottom: auto;
    height: var(--verb-vv-height, 100dvh);
  }
  /* 🔴 THE SHEET PAINTS ITS OWN BACKGROUND PAST WHERE THE BROWSER SAYS IT ENDS.
     Brave on Android tells the page the visible area is 48px shorter than it is
     (innerHeight 660 against Chrome's 708, visual viewport 365 against 413, the
     same phone and the same page, 2026-10-08) while still painting the page in
     that strip, so the sheet stopped short of the keyboard and the host page
     showed through underneath. No number JavaScript can read is right there, so
     nothing JavaScript measures can close it, and chasing one browser at a time
     is a game with no end. Instead the sheet extends in its own colour, which is
     correct whatever the browser reports: where the number is right the skirt
     hangs below the screen and is never seen; where it is short the strip fills
     with the sheet, not with somebody else's page.
     160px is far more than any inset a navigation bar or gesture area takes, and
     costs nothing when it lies off-screen. The page is held still while the sheet
     is open, so it adds no scrollable area. */
  .panel::after {
    content: ""; position: absolute; left: 0; right: 0; top: 100%;
    height: 160px; background: var(--verb-bg);
  }
  .panel[data-display-mode="dialog"] {
    /* right and bottom are reset explicitly, because the centred rule sets
       top and left as percentages and a sheet pinned by all four edges at
       once is a sheet whose height nobody controls. */
    top: 0; left: 0; right: auto; bottom: auto;
    width: 100vw;
    top: var(--verb-vv-top, 0);
    height: var(--verb-vv-height, 100dvh);
    max-width: 100vw; max-height: var(--verb-vv-height, 100dvh);
    border-radius: 0; border: none;
    box-shadow: none;
    transform: scale(1); /* no slide-from-a-corner on a full-screen sheet */
  }
  /* Nothing to dim behind something covering the whole screen. */
  .scrim { display: none; }
  .panel[data-display-mode="dialog"][data-open="true"] { transform: scale(1); }
  .launcher:not([data-inline="true"]),
  .launcher[data-position]:not([data-inline="true"]) {
    right: var(--verb-offset-x, 14px); bottom: var(--verb-offset-y, 14px); left: auto; top: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .panel, .scrim { transition: none; }
}

.header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--verb-border);
  background: var(--verb-surface);
}

/* Test and dry-run sessions are labelled permanently, not with a dismissible
   toast. Mistaking a simulated session for a real one (or the reverse) is the
   failure this whole environment split exists to prevent, so the label has to
   be impossible to miss and impossible to close. */
.mode-banner {
  display: flex; align-items: center; gap: 7px;
  padding: 7px 16px;
  font-size: 12px; font-weight: 600;
  background: var(--verb-gold-soft);
  color: var(--verb-text);
  border-bottom: 1px solid var(--verb-gold-ring);
}
.mode-banner[data-dry="true"] {
  background: var(--verb-gold);
  /* Solid brand fill, same contrast risk as the launcher and the buttons:
     see the note there. Computed per brand colour, not assumed. */
  color: var(--verb-gold-fg, var(--verb-text));
}
.mode-banner-dot {
  width: 6px; height: 6px; border-radius: 999px;
  background: var(--verb-gold-hover); flex: none;
}
.mode-banner[data-dry="true"] .mode-banner-dot { background: var(--verb-gold-fg, var(--verb-text)); }
.mode-banner span:last-child { font-weight: 400; color: var(--verb-text-muted); }
.mode-banner[data-dry="true"] span:last-child { color: var(--verb-gold-fg, var(--verb-text)); opacity: .75; }

.msg[data-dry="true"] {
  border: 1px dashed var(--verb-gold-ring);
  background: transparent;
}
.title { font-size: 14px; font-weight: 650; letter-spacing: -0.01em; }

/* The cards shown before anybody has typed. A question somebody can press is
   worth more than an empty box, which is the difference between an assistant
   people try and one they look at. */
.starters { display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 0 2px; }
.starter {
  font: inherit; font-size: 13px; line-height: 1.35; text-align: left;
  padding: 7px 11px; border-radius: 999px;
  border: 1px solid var(--verb-border);
  background: var(--verb-surface); color: var(--verb-text);
  transition: background-color .12s, border-color .12s, transform .12s ease;
}
.starter:hover {
  background: var(--verb-gold-soft);
  border-color: var(--verb-gold-ring);
  transform: translateY(-1px);
}
.starter:focus-visible { outline: 3px solid var(--verb-gold-ring); outline-offset: 2px; }
/* A link card. Anchors bring their own colour and underline; a starter is a
   card whether it asks something or goes somewhere, so those are reset and the
   arrow does the telling instead.

   🔴 THE LITERAL CHARACTER, NOT A CSS ESCAPE, AND THAT IS THE FIX RATHER THAN
   THE STYLE. This held a CSS escape for U+2197 and shipped as a control
   character followed by the text 97, which is what a visitor saw on every link
   starter: a tofu box and two digits. A non-raw string in a patch script had
   read the first half of that escape as an OCTAL one, resolved it to 0x11, and
   left the rest behind as text.

   That is the third time this codebase has been bitten by an escape sequence
   surviving as a control character; see the note in packages/authoring's
   review.mjs about a word-boundary escape becoming BACKSPACE, twice. The
   character itself cannot be re-mangled by anything that escapes strings, and
   house-style.test.ts now fails the build on any control character in source,
   which is the part that stops a fourth time.

   Note also that this comment cannot spell the escape out: a backslash before
   a digit is a legacy octal escape, which is a hard SyntaxError inside a
   template literal, and this file is one.
*/
.starter-link { text-decoration: none; display: inline-flex; align-items: center; gap: 5px; }
.starter-link::after { content: "↗"; font-size: 11px; opacity: .55; }

/* 🔴 THE HIGHLIGHT IS A SLOW SHEEN, NOT A RAINBOW, AND THAT IS A DELIBERATE
   CLIMBDOWN FROM WHAT WAS ASKED FOR. This renders on somebody else's product,
   next to their own buttons, and a cycling rainbow is the one effect guaranteed
   to clash with every brand it lands on. It also reads as an advertisement,
   which is exactly wrong for the card a customer most wants clicked.

   So: their own brand gold, moving. It draws the eye for the same reason and
   cannot fight a palette it is already made from.

   🔴 AND IT STOPS COMPLETELY UNDER prefers-reduced-motion. A looping animation
   is a vestibular trigger and this one is pure decoration, so it does not
   degrade, it goes away and leaves the tint behind. */
.starter-glow {
  border-color: var(--verb-gold-ring);
  background:
    linear-gradient(var(--verb-gold-soft), var(--verb-gold-soft)) padding-box,
    linear-gradient(90deg,
      var(--verb-gold-soft) 0%,
      var(--verb-gold-ring) 25%,
      var(--verb-gold) 50%,
      var(--verb-gold-ring) 75%,
      var(--verb-gold-soft) 100%) border-box;
  border: 1px solid transparent;
  background-size: 100% 100%, 300% 100%;
  animation: verb-sheen 4.5s linear infinite;
}
@keyframes verb-sheen {
  from { background-position: 0 0, 0 0; }
  to   { background-position: 0 0, 300% 0; }
}

/* 🔴 A HIGHLIGHTED STARTER FOLLOWS THE SITE'S GLOW SETTING RATHER THAN CARRYING
   ITS OWN. Two separate controls for "the launcher is rainbow" and "the cards
   are gold" is two ways to end up with a widget that disagrees with itself.
   Brand and off both keep the sheen above, which already IS the brand; only
   rainbow swaps the ramp, and it uses the same registered angle so the ring
   and the launcher turn together. */
/* 🔴 THE SAME SHEEN AS ABOVE, IN SPECTRUM RATHER THAN GOLD. It was a spinning
   conic gradient keyed to the registered angle, which carried the identical
   invalid-at-computed-value-time risk: an unresolvable var() there does not
   fall back to the gold sheen, it makes the starter's background transparent
   and the card disappears into the panel. A moving background-position on a
   static gradient has no such chain. */
[data-glow="rainbow"] .starter-glow {
  background:
    linear-gradient(var(--verb-bg), var(--verb-bg)) padding-box,
    linear-gradient(90deg,
      #ff4d4d 0%, #ff9f1c 16%, #ffe45c 32%, #4ade80 48%,
      #22d3ee 64%, #6366f1 80%, #d946ef 100%) border-box;
  background-size: 100% 100%, 300% 100%;
  animation: verb-sheen 4.5s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .starter-glow { animation: none; background: var(--verb-gold-soft); border-color: var(--verb-gold-ring); }
  [data-glow="rainbow"] .starter-glow { animation: none; }
}
/* New chat sits beside Close and is styled identically, on purpose: they are
   the same kind of control, one ends the conversation and one ends the panel,
   and giving either of them more weight than the composer would be wrong. */
.header-actions { display: flex; align-items: center; gap: 2px; flex: none; }
.close, .newchat {
  border: none; background: transparent; padding: 6px;
  border-radius: var(--verb-radius-sm); line-height: 0;
  color: var(--verb-text-muted);
}
.close:hover, .newchat:hover { background: var(--verb-surface-alt); color: var(--verb-text); }
.close:focus-visible, .newchat:focus-visible {
  outline: 2px solid var(--verb-gold-ring); outline-offset: 1px;
}
/* Kept for the [hidden] attribute even though nothing sets it today: stated
   rather than left to the browser default, because the day somebody gives
   .newchat its own display value that default silently stops applying. */
.newchat[hidden] { display: none; }

.transcript {
  flex: 1; overflow-y: auto; padding: 16px;
  /* Reaching the end of the conversation must not hand the drag to the page
     behind. Without this the host page scrolls once the transcript runs out. */
  overscroll-behavior: contain;
  display: flex; flex-direction: column; gap: 12px;
  font-size: 14px; line-height: 1.55;
}

/* A message is a row: avatar beside the assistant's bubble, edit button beside
   the user's. The bubble itself stays a single text node, so reading it back
   (the live region, an edit) yields exactly what was said and nothing else. */
/* 🔴 THE ROW IS A FLEX LINE OF AVATAR AND BUBBLE, SO A THIRD CHILD LANDS
      BESIDE THE ANSWER, NOT UNDER IT. That is exactly what the feedback bar
      did on the first build: "Was this helpful?" sat to the right of the
      bubble and squeezed a perfectly ordinary reply into a five-word column.
      Wrapping is enabled here and .fb claims a whole line below, which is
      the only place a footer for a message can go. Nothing else in a row ever
      wraps: the avatar is 26px and fixed, the bubble caps at 88% and shrinks
      before it would push past the end. */
.row { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 8px; max-width: 100%; }
.row[data-role="user"] { justify-content: flex-end; }
/* When it was said: the visitor's own local time, on its own line under the bubble. The 34px
   indent is the avatar (26px) plus the row gap (8px), so it lines up under the text. */
.row > .stamp { flex-basis: 100%; margin: -4px 0 0; padding: 0 34px; font-size: 11px; line-height: 1.3; color: var(--verb-text-muted); }
.row[data-role="user"] > .stamp { text-align: right; }
/* The heading before a new calendar day: Today, Yesterday, a weekday, or a date. */
.day { display: flex; align-items: center; gap: 10px; margin: 6px 0 2px; font-size: 11px; font-weight: 600; letter-spacing: .02em; color: var(--verb-text-muted); }
.day::before, .day::after { content: ""; flex: 1; height: 1px; background: var(--verb-border); }
.row[data-role="assistant"] { justify-content: flex-start; }

.avatar {
  flex: none; width: 26px; height: 26px; margin-top: 2px;
  border-radius: 999px; overflow: hidden;
  display: grid; place-items: center;
  background: var(--verb-gold);
  /* Same computed pair as the launcher: text on the brand fill is never
     assumed, it is whichever of near-black/near-white passes contrast. */
  color: var(--verb-gold-fg, var(--verb-text));
  font-size: 12px; font-weight: 700; line-height: 1;
  user-select: none;
}
.avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.avatar-blob { background: transparent; border-radius: 0; overflow: visible; }
.avatar-blob svg { width: 100%; height: 100%; display: block; }

/* The visitor's own marker. Quiet on purpose: it says which side of the
   conversation a message is on, and nothing about who they are. Never the
   brand fill, or the eye reads two assistants talking to each other. */
.avatar-user {
  background: var(--verb-surface-alt);
  color: var(--verb-text-muted);
  box-shadow: inset 0 0 0 1px var(--verb-border);
}

/* The assistant's face beside its name in the header. Slightly smaller than
   the transcript's, and with no top margin, because it is centred against a
   line of text rather than against the first line of a paragraph. */
.header-id { display: flex; align-items: center; gap: 8px; min-width: 0; }
.avatar-header { width: 22px; height: 22px; margin-top: 0; font-size: 11px; }
.avatar-header svg { display: block; }

/* 🔴 min-width: 0 IS LOAD-BEARING, 2026-09-24. .msg is a flex item of .row,
   and a flex item's default min-width is auto, not 0: the browser will not
   shrink it below its CONTENT's own natural width, no matter what max-width
   says. Plain text never has a wide natural width, so this went unnoticed
   until a table did, on Resuelve's live site: its .md-table-wrap has
   overflow-x: auto, but that only scrolls once .msg has actually been
   capped to 88%. Without this line the row itself grows past the panel
   instead, and there is nothing left to put a scrollbar on. */
.msg { max-width: 88%; min-width: 0; padding: 10px 13px; border-radius: var(--verb-radius); white-space: pre-wrap; overflow-wrap: anywhere; }
/* Structured answers: a heading, a bullet list, a paragraph.
   🔴 The bubble is white-space: pre-wrap, which is what makes a wrapped
      sentence keep its own line breaks. That inherits into these blocks, so
      spacing here is done with margins and the renderer never emits the
      newline characters BETWEEN blocks: leaving them in would add a blank
      line on top of every margin.
   🔴 NO BACKTICKS ANYWHERE IN THIS FILE. It is one big template literal, so a
      backtick in a comment closes the string and the error surfaces as a
      TS1005 on a line that looks fine. */
.msg > .md-p + .md-p, .msg > .md-p + .md-h, .msg > .md-ul + .md-h,
.msg > .md-p + .md-ul, .msg > .md-ul + .md-p,
.msg > * + .md-table-wrap, .msg > .md-table-wrap + * { margin-top: 8px; }
.md-h {
  font-weight: 600; font-size: 0.95em; letter-spacing: -0.005em;
  /* Not a bigger font. In a 300px panel an h3-sized heading shouts, and the
     model uses ### for "here is a small group of fields", not for a title. */
}
.md-ul {
  margin: 0; padding: 0; list-style: none;
  /* white-space normal INSIDE the list: a bullet's text should wrap under
     itself, not preserve the source line's own indentation. */
  white-space: normal;
}
.md-ul > li {
  position: relative; padding-left: 14px;
}
.md-ul > li + li { margin-top: 3px; }
.md-ul > li::before {
  content: ""; position: absolute; left: 3px; top: 0.62em;
  width: 4px; height: 4px; border-radius: 50%;
  background: currentColor; opacity: 0.45;
}

/* A model's comparison table. The panel is often narrower than the table it
   is asked to hold, so the wrap scrolls sideways rather than squeezing every
   column unreadable; the table itself never shrinks below its own content.

   🔴 width: 100% WAS THE BUG, 2026-09-24, CAUGHT ON RESUELVE'S REAL DATA. It
      pins the table to the wrapper's width, so table-layout: auto's column
      algorithm has no choice but to shrink columns to fit inside it, no
      matter how little room that leaves. A short header like "Model" has
      nothing forcing it wider, so it was squeezed to a sliver, and its body
      cells, lacking their own white-space rule, wrapped using the anywhere
      value .msg sets for its own prose (inherited into every cell): "Claude"
      broke into "Claud" / "e". min-width: 100% keeps a short table filling
      the bubble, same as before, but no longer forbids a wide one from
      growing past it, which is the one thing that makes overflow-x: auto
      above ever have something to scroll. nowrap on every cell, not just
      the header, is what stops the wrapping in the first place. */
.md-table-wrap {
  overflow-x: auto; max-width: 100%; -webkit-overflow-scrolling: touch;
  border: 1px solid var(--verb-border); border-radius: var(--verb-radius-sm);
}
.md-table {
  border-collapse: collapse; min-width: 100%;
  font-size: 12.5px; line-height: 1.4;
}
.md-table th, .md-table td {
  padding: 6px 10px; text-align: left; border-bottom: 1px solid var(--verb-border);
  white-space: nowrap; overflow-wrap: normal;
}
.md-table tr:last-child td { border-bottom: none; }
.md-table th {
  background: var(--verb-surface-alt); font-weight: 600; color: var(--verb-text-muted);
}
.md-table td { color: var(--verb-text); }

/* 🔴 THE DERIVED VALUE IS THE FALLBACK, NOT A DEFAULT SITTING IN THE TOKEN
   TABLE. Both bubbles follow something today: the visitor's own follows the
   panel background and the assistant's follows the brand colour. If these
   tokens were given fixed values in @verb/theme, that would stop, and a site
   that picked a dark background would get a dark panel with two pale bubbles
   floating in it. Unset here means "keep following", which is a third state a
   default cannot express.

   🔴 AND THE TEXT COLOUR MOVES WITH THE FILL. A bubble is the only surface in
   this widget carrying a person's actual words rather than a label, so it is
   the worst one to leave to chance: a deep blue reply with near-black text is
   a message nobody can read, and the owner who chose the colour would not find
   out, because the dashboard renders none of this. deriveBubbleTokens computes
   it from the fill the same way the launcher's does. */
.msg[data-role="user"] {
  background: var(--verb-bubble-user, var(--verb-surface-alt));
  color: var(--verb-bubble-user-fg, var(--verb-text));
}
.msg[data-role="assistant"] {
  background: var(--verb-bubble-assistant, var(--verb-gold-soft));
  color: var(--verb-bubble-assistant-fg, var(--verb-text));
}

/* The chat-shape tell: each bubble tucks its own corner, so a busy transcript
   reads by silhouette before anybody reads a name. One corner each, 4px, quiet
   enough to be felt rather than noticed. */
.msg[data-role="assistant"] { border-top-left-radius: 4px; }
.msg[data-role="user"] { border-top-right-radius: 4px; }

/* Present but quiet, and only on hover or keyboard focus. A pencil on every
   line turns a conversation into a form. */
.edit {
  flex: none; align-self: center; padding: 5px;
  border: none; background: transparent; line-height: 0;
  border-radius: var(--verb-radius-sm);
  color: var(--verb-text-muted);
  opacity: 0; transition: opacity .12s, background-color .12s;
  order: -1; /* to the LEFT of the user's bubble, outside the text */
}
.row[data-role="user"]:hover .edit, .edit:focus-visible { opacity: 1; }
.edit:hover { background: var(--verb-surface-alt); color: var(--verb-text); }
.edit:focus-visible { outline: 2px solid var(--verb-gold-ring); outline-offset: 1px; }
.panel[data-busy="true"] .edit { display: none; }

.editor { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 7px; }
.editor-input {
  width: 100%; resize: vertical; padding: 9px 11px; font: inherit; font-size: 14px;
  border: 1px solid var(--verb-gold); border-radius: var(--verb-radius);
  background: var(--verb-bg); color: var(--verb-text);
}
.editor-input:focus-visible { outline: 2px solid var(--verb-gold-ring); outline-offset: -1px; }
.editor-actions { display: flex; gap: 7px; justify-content: flex-end; }

/* 🔴 The gap before the first token is where people decide we are broken. A
   turn that calls a tool produces no text at all for several seconds. */
.thinking {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 13px; border-radius: var(--verb-radius);
  background: var(--verb-gold-soft);
  font-size: 13px; color: var(--verb-text-muted);
}
.thinking-label { font-style: italic; }
.dots { display: inline-flex; gap: 3px; }
.dots i {
  width: 4px; height: 4px; border-radius: 999px;
  background: var(--verb-text-muted);
  animation: verb-blink 1.2s infinite ease-in-out both;
}
.dots i:nth-child(2) { animation-delay: .16s; }
.dots i:nth-child(3) { animation-delay: .32s; }
@keyframes verb-blink {
  0%, 80%, 100% { opacity: .25; }
  40% { opacity: 1; }
}

@keyframes verb-spin { to { transform: rotate(360deg); } }

/* Movement is decoration here; the words carry the meaning either way. */
@media (prefers-reduced-motion: reduce) {
  .dots i, .tool-icon { animation: none !important; }
  .dots i { opacity: .6; }
}

.empty { color: var(--verb-text-muted); font-size: 13px; margin: auto 0; text-align: center; }

/* An icon and a label, nothing around them. A tool call is not an alert and
   does not need a border or a fill to say so; it needs to be legible and then
   get out of the way. Same shape as Marker in the dashboard. */
.tool {
  /* 🔴 flex, NOT inline-flex. As inline-flex every call in an expanded group
     ran onto the same line, so three tools rendered as one unbroken string:
     "Availability schedules list Event types list Profile get". A list of what
     the agent did is a list, and it reads top to bottom. */
  display: flex; align-items: center; gap: 7px;
  font-size: 12px; font-weight: 500; color: var(--verb-text-muted);
  padding: 3px 0;
}
.tool-icon { flex: none; color: var(--verb-text-muted); }
.tool[data-status="running"] .tool-icon { animation: verb-spin .8s linear infinite; }
.tool[data-status="ok"] .tool-icon, .tool[data-status="dry_run"] .tool-icon { color: var(--verb-success); }
.tool[data-status="ok"] .tool-label, .tool[data-status="dry_run"] .tool-label { color: var(--verb-text); }
.tool[data-status="error"] .tool-icon, .tool[data-status="denied"] .tool-icon { color: var(--verb-danger); }
.tool[data-status="unconfirmed"] .tool-icon, .tool[data-status="changed"] .tool-icon { color: var(--verb-gold); }
.tool[data-status="unconfirmed"] .tool-label, .tool[data-status="changed"] .tool-label { color: var(--verb-text); }

/* The expanded record of a finished turn.

   🔴 A PANEL, NOT LOOSE TEXT UNDER A LINK. Opened, this is the answer to "what
   was it doing for those thirty seconds", which is the question anybody asks
   of an agent that went quiet. Giving it an edge and a heading makes it read
   as a record rather than as debug output that leaked into the conversation.

   While the turn RUNS none of this applies: the list is bare and only the
   newest chip shows, because a live line saying what is happening right now is
   worth more than a box that grows and pushes the answer down the screen. */
.tools[data-state="done"][data-open="true"] .tool-list {
  margin-top: 4px;
  padding: 9px 11px;
  border: 1px solid var(--verb-border);
  border-radius: var(--verb-radius-sm);
  background: var(--verb-surface);
}
/* A decided confirmation, once it has folded into the turn's record.

   🔴 HIDDEN WHILE THE TURN RUNS, NOT JUST WHEN COLLAPSED. The card moves in
   here the instant you press Cancel or Confirm, which is while the group is
   still in its running state, so without this it would simply carry on sitting
   in the conversation in a new parent. The point of moving it is that the
   answer to the question leaves the screen once the question is answered.

   Flatter in here than it was in the transcript: no shadow and no outer
   border, because the panel around it already provides both, and a card
   inside a card reads as a mistake. */
.tools .confirm { display: none; }
.tools[data-state="done"][data-open="true"] .confirm {
  display: block;
  margin-top: 7px;
  box-shadow: none;
}
.tools .confirm .confirm-head { background: none; }

.tool-list-head {
  display: none;
  margin: 0 0 5px;
  font-family: var(--verb-font-mono);
  font-size: 9.5px; font-weight: 600;
  letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--verb-text-muted);
}
.tools[data-state="done"][data-open="true"] .tool-list-head { display: block; }

/* One turn's tools, folded away. While the turn RUNS, only the newest chip
   shows: a live line saying what is happening right now, rather than a list
   that grows a row per call and pushes the answer down. When the turn ends the
   whole thing becomes a single summary, closed, that says how many ran.

   🔴 The summary row is hidden WHILE RUNNING and the list is hidden AFTERWARDS,
      which is why both rules key off data-state rather than data-open alone.
      Showing a "Show details" button next to a live spinner would be offering
      to reveal something that is already on screen. */
.tools { align-self: flex-start; display: flex; flex-direction: column; max-width: 100%; }
.tools[data-state="running"] .tool-summary { display: none; }
/* Beside the thumbs and ahead of them, not on a line of its own. The feedback
   row owns the spacing; the summary just joins it as its first child.

   🔴 NO margin-left:auto HERE, AND THERE WAS. It was written for a version
      that pushed the button to the far right of the row, and it survived the
      decision to put it first, where an auto margin on the leading item shoves
      the whole row right instead. Nothing looked wrong in a DOM test, because
      there is no layout engine in one. */
.fb .tool-summary { align-self: center; padding: 0; }
.tools[data-state="running"] .tool:not(:last-child) { display: none; }
.tools[data-state="done"][data-open="false"] .tool-list { display: none; }

.tool-summary {
  align-self: flex-start; display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 0; border: 0; background: none; cursor: pointer;
  font: inherit; font-size: 12px; font-weight: 500;
  color: var(--verb-text-muted); text-align: left;
}
.tool-summary:hover { color: var(--verb-text); }
/* Keyboard users must be able to see what they are about to open. */
.tool-summary:focus-visible { outline: 2px solid var(--verb-gold); outline-offset: 2px; border-radius: 4px; }

/* Rendered inline in the transcript, not as a modal: a modal over someone
   else's site is intrusive, and the user needs the conversation context to
   judge what they are approving.

   🔴 THE GOLD RULE DOWN THE LEFT EDGE IS GONE, AND WHAT IT WAS DOING IS NOT.
   Its stated job was marking this as the one card in the transcript waiting
   for you, which is a real job done badly: a stripe of colour on one side is
   invisible to anyone not already looking for it, means nothing on first
   sight, reads as decoration, and disappears entirely for a customer whose
   brand colour is close to the surface behind it. It also made the card look
   like a quoted block rather than something to act on.

   The signal moved into words. "Needs your approval" sits above the action in
   the header, which cannot be missed, cannot be mistaken for styling, and is
   read out loud by a screen reader in the order it matters.

   What is left is an ordinary card with three real parts: what is being asked,
   the exact values it will run with, and the two buttons. */
.confirm {
  align-self: stretch;
  /* 🔴 flex: none, AND WITHOUT IT THIS CARD COLLAPSES TO A LINE. The
     transcript is a flex column, and a flex item whose overflow is anything
     but visible gets an automatic minimum size of ZERO instead of its content
     height. So once the transcript had enough in it to scroll, the browser
     shrank the tallest thing in the column, which is this card, down to a 1px
     rule.

     Reported twice as "it just keeps loading and no confirmation appears".
     The card was always there: the server logged the request and then denied
     it with no_response and a null latency, because nobody can answer
     something one pixel tall. The overflow below is what rounds the header
     corners, so it stays and this line pays for it.

     NO BACKTICKS IN THIS FILE. This comment had eight on the first attempt
     and would have broken the build for the fourth time. */
  flex: none;
  border: 1px solid var(--verb-border);
  border-radius: var(--verb-radius);
  background: var(--verb-bg);
  overflow: hidden;
  box-shadow: 0 1px 2px rgb(28 25 23 / 5%), 0 8px 20px -12px rgb(28 25 23 / 18%);
}

.confirm-head {
  padding: 11px 13px 10px;
  background: var(--verb-surface);
  border-bottom: 1px solid var(--verb-border);
}
.confirm-eyebrow {
  margin: 0 0 3px;
  display: flex; align-items: center; gap: 6px;
  font-family: var(--verb-font-mono);
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.09em; text-transform: uppercase;
  color: var(--verb-gold-hover);
}
/* The dot is the only ornament left, and it is 5px rather than a full edge. */
.confirm-eyebrow::before {
  content: ""; width: 5px; height: 5px; border-radius: 999px;
  background: var(--verb-gold); flex: none;
}
.confirm-title {
  margin: 0;
  font-size: 13.5px; font-weight: 650; line-height: 1.35;
  color: var(--verb-text);
}

.confirm-warning {
  margin: 0;
  padding: 9px 13px;
  font-size: 12px; line-height: 1.45;
  color: var(--verb-danger);
  background: var(--verb-danger-soft);
  border-bottom: 1px solid var(--verb-border);
}

/* 🔴 LABEL ABOVE VALUE, NOT BESIDE IT. The old layout put a fixed 84px label
   column next to the value, so anything long, and the commonest argument in
   this product is a uuid, wrapped into a ragged two-line block beside a label
   with nothing under it. Stacking gives every value the full width.

   🔴 AND NOTHING IS TRUNCATED. A uuid is ugly and it stays whole: this card is
   the thing a person checks before something changes their data, so the value
   shown has to be the value that runs, in full, with no ellipsis hiding the
   middle of it. */
.confirm-args { margin: 0; padding: 2px 13px; font-size: 12.5px; }
.confirm-args div { padding: 9px 0; }
.confirm-args div + div { border-top: 1px solid var(--verb-border); }
.confirm-args dt {
  font-family: var(--verb-font-mono);
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--verb-text-muted);
}
.confirm-args dd {
  margin: 4px 0 0;
  font-family: var(--verb-font-mono);
  font-size: 12.5px; line-height: 1.5;
  color: var(--verb-text);
  overflow-wrap: anywhere;
}

/* The owner's view of what an action will send, on a test site only. Quiet on purpose:
   it is for the person building the action, and a visitor never sees it. */
.confirm-debug { margin: 0 13px 11px; font-size: 11.5px; color: var(--verb-text-muted); }
.confirm-debug summary { cursor: pointer; }
.confirm-debug pre {
  margin: 6px 0 0; padding: 8px; max-height: 140px; overflow: auto;
  background: var(--verb-surface-alt); border-radius: var(--verb-radius-sm);
  font: 11px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace;
  white-space: pre-wrap; overflow-wrap: anywhere;
}

.confirm-actions {
  display: flex; gap: 8px; justify-content: flex-end;
  padding: 11px 13px;
  border-top: 1px solid var(--verb-border);
  background: var(--verb-surface);
}

/* ── Fields on a card, docs/40 section 5 ──────────────────────────────────
   Every colour below is a theme token, and the selected states use the gold
   family, which deriveBrandTokens recomputes from a customer's own brand
   colour. So a site that sets its brand gets fields in its brand, with no
   second setting to find. */

.vstep-progress { padding: 12px 13px 2px; display: flex; flex-direction: column; gap: 7px; }
.vstep-label {
  margin: 0;
  font-family: var(--verb-font-mono);
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--verb-text-muted);
}
.vstep-bar { display: flex; gap: 4px; }
.vstep-seg {
  flex: 1; height: 4px; border-radius: 999px;
  background: var(--verb-surface-alt);
  transition: background 160ms ease;
}
.vstep-seg[data-state="done"] { background: var(--verb-gold-ring); }
.vstep-seg[data-state="current"] { background: var(--verb-gold); }

.vfields { padding: 12px 13px 4px; display: flex; flex-direction: column; gap: 14px; }
.vfields[hidden], .vstep-review[hidden] { display: none; }
.vfield { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.vfield-label { font-size: 12.5px; font-weight: 600; color: var(--verb-text); }
.vfield-hint { margin: -2px 0 0; font-size: 11.5px; line-height: 1.4; color: var(--verb-text-muted); }

.vfield-input {
  width: 100%; box-sizing: border-box;
  font: inherit; font-size: 13px; line-height: 1.3;
  min-height: 38px; padding: 8px 11px;
  border: 1px solid var(--verb-border-strong); border-radius: var(--verb-radius-sm);
  background: var(--verb-bg); color: var(--verb-text);
  color-scheme: light;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.vfield-input::placeholder { color: var(--verb-text-muted); }
.vfield-input:hover { border-color: var(--verb-text-muted); }
.vfield-input:focus { outline: none; border-color: var(--verb-gold); box-shadow: 0 0 0 3px var(--verb-gold-soft); }
.vfield-input[readonly], .vfield-input:disabled { background: var(--verb-surface); color: var(--verb-text-muted); }
textarea.vfield-input { min-height: 76px; resize: vertical; }

/* A select drawn like the inputs, with its own chevron. */
select.vfield-input {
  appearance: none; -webkit-appearance: none;
  padding-right: 34px; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235c6472' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 10px center; background-size: 16px;
}

/* Radio options as chips: every choice visible, the chosen one in the brand. */
.vfield-radios { display: flex; flex-wrap: wrap; gap: 6px; }
.vfield-radio {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 7px 12px; min-height: 34px; box-sizing: border-box;
  border: 1px solid var(--verb-border-strong); border-radius: var(--verb-radius-sm);
  background: var(--verb-bg); color: var(--verb-text);
  font-size: 13px; cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
}
.vfield-radio:hover { border-color: var(--verb-text-muted); }
.vfield-radio:has(input:checked) { border-color: var(--verb-gold); background: var(--verb-gold-soft); }
.vfield-radio:has(input:focus-visible) { box-shadow: 0 0 0 3px var(--verb-gold-soft); }
.vfield-radio input {
  appearance: none; -webkit-appearance: none; margin: 0; flex: none;
  width: 15px; height: 15px; border-radius: 999px;
  border: 1.5px solid var(--verb-border-strong); background: var(--verb-bg);
}
.vfield-radio input:checked { border-color: var(--verb-gold); box-shadow: inset 0 0 0 3.5px var(--verb-gold); }
.vfield-radio input:disabled, .vfield-check input:disabled { cursor: default; }

.vfield-check { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; cursor: pointer; }
.vfield-check input {
  appearance: none; -webkit-appearance: none; margin: 0; flex: none;
  width: 16px; height: 16px; border-radius: 4px;
  border: 1.5px solid var(--verb-border-strong); background: var(--verb-bg);
}
.vfield-check input:checked {
  border-color: var(--verb-gold); background-color: var(--verb-gold);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231c1917' stroke-width='3.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 6 9 17l-5-5'/%3E%3C/svg%3E");
  background-size: 12px; background-position: center; background-repeat: no-repeat;
}
.vfield-check input:focus-visible { box-shadow: 0 0 0 3px var(--verb-gold-soft); }

/* 🔴 Visually hidden, never display:none: it still opens the picker, takes
   keyboard focus and is announced. The label below is what gets drawn. */
.vfield-file-native {
  position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
.vfield-drop {
  display: flex; align-items: center; gap: 11px;
  padding: 12px; box-sizing: border-box;
  border: 1.5px dashed var(--verb-border-strong); border-radius: var(--verb-radius-sm);
  background: var(--verb-surface); cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
}
.vfield-drop:hover, .vfield-drop[data-drag="true"] { border-color: var(--verb-gold); background: var(--verb-gold-soft); }
.vfield-file-native:focus-visible + .vfield-drop { box-shadow: 0 0 0 3px var(--verb-gold-soft); border-color: var(--verb-gold); }
.vfield-drop[hidden], .vfield-file[hidden] { display: none; }
.vfield-drop-icon {
  width: 30px; height: 30px; flex: none; padding: 6px; box-sizing: border-box;
  border-radius: var(--verb-radius-sm); background: var(--verb-bg);
  border: 1px solid var(--verb-border);
  fill: none; stroke: var(--verb-text-muted); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
}
.vfield-drop-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.vfield-drop-main { font-size: 13px; font-weight: 600; color: var(--verb-text); }
.vfield-drop-sub { font-size: 11.5px; color: var(--verb-text-muted); }

.vfield-file {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 8px 8px 8px; box-sizing: border-box;
  border: 1px solid var(--verb-border-strong); border-radius: var(--verb-radius-sm);
  background: var(--verb-bg);
}
.vfield-file-thumb { width: 38px; height: 38px; flex: none; object-fit: cover; border-radius: 6px; }
.vfield-file-badge {
  width: 38px; height: 38px; flex: none; display: grid; place-items: center;
  border-radius: 6px; background: var(--verb-surface-alt);
  font-family: var(--verb-font-mono); font-size: 10px; font-weight: 700; color: var(--verb-text-muted);
}
.vfield-file-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.vfield-file-name { font-size: 12.5px; font-weight: 600; color: var(--verb-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vfield-file-size { font-size: 11.5px; color: var(--verb-text-muted); }
.vfield-file-remove {
  flex: none; padding: 5px 9px; border-radius: 6px;
  border: 1px solid transparent; background: transparent;
  font: inherit; font-size: 12px; font-weight: 600; color: var(--verb-text-muted); cursor: pointer;
}
.vfield-file-remove:hover { background: var(--verb-surface-alt); color: var(--verb-text); }

.vfield-error { margin: 0; font-size: 11.5px; line-height: 1.4; color: var(--verb-danger); }
.vfield-error:empty { display: none; }
.vfield[data-invalid="true"] .vfield-input,
.vfield[data-invalid="true"] .vfield-drop { border-color: var(--verb-danger); }
.vfield[data-invalid="true"] .vfield-input:focus { box-shadow: 0 0 0 3px var(--verb-danger-soft); }

.vstep-review { padding-top: 6px; }

.confirm-problem:empty { display: none; }
.confirm-problem {
  margin: 0; padding: 8px 13px 0; font-size: 11.5px; line-height: 1.4; color: var(--verb-text-muted);
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn[hidden] { display: none; }

.btn { padding: 7px 14px; border-radius: var(--verb-radius-sm); border: 1px solid transparent; font-size: 13px; font-weight: 600; }
.btn:focus-visible { outline: 2px solid var(--verb-gold-ring); outline-offset: 2px; }
.btn-primary { background: var(--verb-gold); color: var(--verb-gold-fg, var(--verb-text)); }
.btn-primary:hover { background: var(--verb-gold-hover); }
.btn-secondary { background: transparent; border-color: var(--verb-border-strong); color: var(--verb-text-muted); }
.btn-secondary:hover { background: var(--verb-surface-alt); color: var(--verb-text); }
.confirm[data-resolved="true"] .confirm-actions { display: none; }

/* The flash Show it leaves behind, so the press always has a visible answer
   even when the card never moved. Ring rather than background: it has to read
   on any customer's surface colour, and it must not change the card's own
   colours, which carry meaning. */
.confirm[data-flash="true"] { animation: verb-confirm-flash 1.2s ease-out; }
@keyframes verb-confirm-flash {
  0%, 100% { box-shadow: 0 1px 2px rgb(28 25 23 / 5%), 0 8px 20px -12px rgb(28 25 23 / 18%); }
  15%, 55% { box-shadow: 0 0 0 3px var(--verb-gold-ring), 0 8px 20px -12px rgb(28 25 23 / 18%); }
}
@media (prefers-reduced-motion: reduce) {
  .confirm[data-flash="true"] { animation: none; outline: 3px solid var(--verb-gold-ring); }
}

/* A decided card recedes rather than disappearing: the record of what was
   approved is worth keeping in the transcript, but it is no longer the thing
   in front of you. */
.confirm[data-resolved="true"] { box-shadow: none; }
.confirm[data-resolved="true"] .confirm-eyebrow { display: none; }

.confirm-outcome {
  margin: 0;
  display: flex; align-items: center; gap: 6px;
  padding: 10px 13px;
  border-top: 1px solid var(--verb-border);
  background: var(--verb-surface);
  font-size: 12px; font-weight: 600;
  color: var(--verb-text-muted);
}
.confirm-outcome[data-decision="allow"] { color: var(--verb-success); }
.confirm-outcome[data-decision="allow"]::before { content: "✓"; }
.confirm-outcome[data-decision="deny"]::before { content: "✕"; }
/* An expired card must not read like a completed one. Left-aligned, normal
   weight, muted: it is an explanation, not a result, and the one thing it has
   to communicate is that nothing happened. */
.confirm-expired {
  display: block;
  font-weight: 400; line-height: 1.45;
  color: var(--verb-text-muted);
}
.confirm-expired::before { content: none; }
.confirm[data-expired="true"] { opacity: 0.75; }

/* The bar that appears above the composer while a confirmation is waiting.
   It exists because the composer is disabled for that whole time and the card
   explaining why can be scrolled out of sight, which leaves a dead input and
   no visible reason: indistinguishable from a crash. */
.pending-bar {
  display: flex; align-items: center; gap: 8px;
  width: 100%; margin: 0; padding: 9px 14px;
  border: none; border-top: 1px solid var(--verb-gold-ring);
  background: var(--verb-gold-soft);
  color: var(--verb-text);
  font: inherit; font-size: 12.5px; text-align: left;
  cursor: pointer;
}
/* The rule the rest of this file already warned about twice, and that this bar
   shipped without on 2026-09-09. The display: flex above BEATS the browser
   default that hides [hidden] elements, so setting .hidden = true in JavaScript
   did nothing at all, and the bar sat there permanently announcing a
   confirmation that was not waiting. See .newchat[hidden] and .counter[hidden]:
   the same trap, stated in the same words, twenty lines apart.
   NO BACKTICKS IN THIS FILE. It is one template literal, and a backtick in a
   comment ends it. That has now broken the build three times. */
.pending-bar[hidden] { display: none; }
.pending-bar:hover { background: var(--verb-gold-ring); }
.pending-bar:focus-visible { outline: 2px solid var(--verb-gold); outline-offset: -2px; }
.pending-dot {
  width: 6px; height: 6px; border-radius: 999px; flex: none;
  background: var(--verb-gold-hover);
  animation: verb-pending-pulse 1.6s ease-in-out infinite;
}
@keyframes verb-pending-pulse { 50% { opacity: .35; } }
@media (prefers-reduced-motion: reduce) { .pending-dot { animation: none; } }
.pending-text { flex: 1; }
.pending-cta { font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }

/* 🔴 flex-end, NOT the default stretch. The textarea grows with what you type,
   up to 120px, and a stretched Send grew with it into a gold column half the
   height of the panel. Aligning to the end also puts the button level with the
   last line of the message, which is where the caret is and where Enter gets
   pressed. */
.composer {
  display: flex; align-items: flex-end; gap: 8px; padding: 12px;
  border-top: 1px solid var(--verb-border); background: var(--verb-surface);
}
.input {
  flex: 1; resize: none; padding: 9px 11px; font: inherit; font-size: 14px;
  border: 1px solid var(--verb-border-strong); border-radius: var(--verb-radius-sm);
  background: var(--verb-bg); color: var(--verb-text);
  max-height: 120px; min-height: 38px;
  overscroll-behavior: contain; /* same reason as the transcript */
}
.input:focus-visible { outline: 2px solid var(--verb-gold-ring); outline-offset: -1px; border-color: var(--verb-gold); }
/* While you are typing, the send button arms itself: the affordance for the
   action you are about to take darkens before you reach for it. */
.composer:focus-within .send { background: var(--verb-gold-hover); }
/* Its own fixed height, matching the composer's resting height, so the button
   is the same size on a one-line question and on a pasted stack trace.
   flex-shrink:0 keeps a long word in the textarea from squeezing the label. */
.send {
  flex: 0 0 auto; height: 38px; padding: 0 15px;
  border-radius: var(--verb-radius-sm); border: none;
  background: var(--verb-gold); color: var(--verb-gold-fg, var(--verb-text));
  font-weight: 600; font-size: 13px; transition: background .12s ease;
}
.send:disabled { opacity: .5; cursor: not-allowed; }
/* The remaining-characters count. Absolute so appearing does not reflow the
   composer, which would nudge the send button sideways mid-sentence. */
.composer { position: relative; }
.counter {
  position: absolute; right: 12px; bottom: -2px;
  font-size: 10.5px; line-height: 1;
  color: var(--verb-text-muted);
  pointer-events: none;
}
.counter[data-full="true"] { color: var(--verb-danger); }
.counter[hidden] { display: none; }
.send:focus-visible { outline: 2px solid var(--verb-gold-ring); outline-offset: 2px; }

/* ── Things from a tool result, shown rather than described ───────────────
   A horizontal strip, because a panel is narrow and a vertical list of eight
   product cards buries the answer that is sitting above it. */
.cards {
  display: flex; gap: 8px; overflow-x: auto; padding: 2px 0 6px;
  margin-left: 34px; scrollbar-width: thin;
}
.card {
  flex: 0 0 148px; display: flex; flex-direction: column;
  border: 1px solid var(--verb-border); border-radius: var(--verb-radius-sm);
  background: var(--verb-bg); overflow: hidden;
  text-decoration: none; color: inherit;
  transition: border-color .12s ease;
}
a.card:hover { border-color: var(--verb-gold); }
a.card:focus-visible { outline: 2px solid var(--verb-gold-ring); outline-offset: 1px; }
.card-img {
  width: 100%; aspect-ratio: 4 / 3; object-fit: cover;
  background: var(--verb-surface-alt); display: block;
}
.card-body { display: flex; flex-direction: column; gap: 2px; padding: 7px 9px 9px; }
.card-title {
  font-size: 12.5px; font-weight: 500; line-height: 1.3; color: var(--verb-text);
  /* Two lines, then ellipsis. A card that grows to fit a long product name
     makes every card in the row that tall. */
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.card-sub {
  font-size: 11px; line-height: 1.35; color: var(--verb-text-muted);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.card-meta {
  display: flex; align-items: baseline; gap: 6px; margin-top: 2px;
  font-size: 12px; color: var(--verb-text);
}
.card-rating { font-size: 11px; color: var(--verb-text-muted); }

/* ── Was that any good? ────────────────────────────────────────────────────
   Quiet by default. This asks the visitor for a favour, so it sits below the
   answer at a smaller size and never competes with it for attention.

   🔴 GREEN AND RED CARRY THE WHOLE STATE, and they are the only two colours in
      the widget that are NOT derived from the site owner's brand token. A
      selected thumb has to be unambiguous at a glance, and a customer whose
      brand colour happens to be red would otherwise ship a panel where the
      approving thumb looks like a complaint. docs/29 section 5b fixes the
      exact values for that reason. */
.fb {
  /* 🔴 A WHOLE LINE OF ITS OWN, UNDER THE ANSWER. The 100% basis forces
     the wrap; shrink is left on so the 34px indent that lines it up with the
     bubble comes out of its width instead of overflowing the row. */
  flex: 1 1 100%;
  display: flex; flex-direction: column; gap: 6px;
  margin: 0 0 2px 34px;
  font-size: 11.5px; color: var(--verb-text-muted);
}
/* Wraps, because this row now carries two things rather than one. "Show
   details · 3 tools · 1 cancelled" plus two thumbs is close to the full width
   of a 400px panel, and the tail is written by the turn rather than by us, so
   there is no length we get to assume. Wrapping drops the thumbs to a second
   line in the worst case; not wrapping pushes them off the edge. */
.fb-row { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.fb-label { line-height: 1.4; }
/* 🔴 The recorded rating, in the same two colours the live thumbs use, so the
   settled state reads as the thing that was just pressed rather than a new
   element that replaced it. A grey "thanks" looks identical either way, and
   somebody who meant to complain cannot tell a complaint was what landed. */
.fb-mark { display: inline-flex; line-height: 0; }
.fb-mark[data-kind="up"]   { color: #34d399; }
.fb-mark[data-kind="down"] { color: #f87171; }
.fb-link { color: var(--verb-gold); text-underline-offset: 2px; }
.fb-thumb {
  display: inline-flex; align-items: center;
  border: 1px solid transparent; background: none; cursor: pointer;
  border-radius: 6px; padding: 3px 5px;
  color: var(--verb-text-muted); line-height: 0;
  transition: color .12s ease, background .12s ease, border-color .12s ease;
}
.fb-thumb:hover:not(:disabled) { background: var(--verb-surface-alt); color: var(--verb-text); }
.fb-thumb:disabled { cursor: default; }
.fb-thumb:focus-visible { outline: 2px solid var(--verb-gold-ring); outline-offset: 1px; }
.fb-thumb[data-kind="up"][data-selected="true"] {
  color: #34d399; border-color: rgba(52,211,153,.4); background: rgba(52,211,153,.12);
}
.fb-thumb[data-kind="down"][data-selected="true"] {
  color: #f87171; border-color: rgba(248,113,113,.4); background: rgba(248,113,113,.12);
}

/* The open box. Its border repeats the rating, so it always shows which way it
   is about to be sent. */
.fb-card {
  display: flex; flex-direction: column; gap: 6px;
  border: 1px solid var(--verb-border); border-radius: 8px; padding: 9px;
  background: var(--verb-surface-alt);
}
.fb-card[data-rating="up"]   { border-color: rgba(52,211,153,.35); }
.fb-card[data-rating="down"] { border-color: rgba(248,113,113,.35); }
.fb-card-label { display: flex; flex-direction: column; gap: 5px; line-height: 1.4; }
.fb-note {
  /* rows=2 on purpose. A large box implies a long answer is expected, and that
     expectation is enough to stop somebody replying at all. */
  width: 100%; box-sizing: border-box; font: inherit; font-size: 12px;
  resize: vertical; padding: 5px 8px; color: var(--verb-text);
  border: 1px solid var(--verb-border-strong); border-radius: 6px;
  background: var(--verb-bg);
}
.fb-note:focus-visible { outline: 2px solid var(--verb-gold-ring); outline-offset: -1px; border-color: var(--verb-gold); }
.fb-actions { display: flex; justify-content: flex-end; gap: 6px; }
.fb-cancel, .fb-submit {
  cursor: pointer; border-radius: 6px; padding: 4px 10px;
  font: inherit; font-size: 11.5px; font-weight: 600;
}
/* Ghost on the left: leaving is never the emphasised choice. */
.fb-cancel {
  border: 1px solid var(--verb-border-strong); background: none; color: var(--verb-text-muted);
}
.fb-cancel:hover { color: var(--verb-text); }
.fb-submit { border: none; background: var(--verb-gold); color: var(--verb-gold-fg, var(--verb-text)); }
.fb-card[data-rating="up"] .fb-submit { background: #10b981; color: #04231a; }
.fb-cancel:focus-visible, .fb-submit:focus-visible { outline: 2px solid var(--verb-gold-ring); outline-offset: 2px; }

/* Long transcripts scroll on a hairline thumb the rest of the time; a default
   browser scrollbar is a grey slab that out-shouts everything above it. */
.transcript::-webkit-scrollbar { width: 10px; }
.transcript::-webkit-scrollbar-track { background: transparent; }
.transcript::-webkit-scrollbar-thumb {
  background: var(--verb-border-strong);
  border-radius: 999px;
  border: 3px solid var(--verb-bg);
}
.transcript::-webkit-scrollbar-thumb:hover { background: var(--verb-text-muted); }

.notice { font-size: 12.5px; color: var(--verb-text-muted); text-align: center; padding: 8px; }
.notice[data-kind="error"] { color: var(--verb-danger); }

/* 🔴 FULL TEXT COLOUR, NOT MUTED. "Powered by Verb" and the build stamp were
   both --verb-text-muted, and the stamp took a further 0.6 opacity on top,
   which put it around 2:1 on the panel: legible on a designer's screen and
   not on anybody else's. Attribution nobody can read is attribution that is
   not there, and a version number exists precisely to be read out when
   something is wrong. */
.footer { font-size: 11px; color: var(--verb-text); text-align: center; padding: 0 0 9px; }
.footer a { color: var(--verb-text); text-decoration: none; }
.footer a:hover { text-decoration: underline; text-underline-offset: 2px; }
/* The build stamp. Still smaller and tracked out, so it reads as a serial
   number rather than as a second piece of our branding on somebody else's
   page, but it is the same ink as the line above it now. */
.build {
  margin-top: 2px;
  font-size: 9px; letter-spacing: 0.06em;
  color: var(--verb-text);
  font-variant-numeric: tabular-nums;
}

.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
`;
